import styled from 'styled-components'

export const CardWrapper = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
`

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 4px;
`

export const CardTitle = styled.div`
  font-size: 18px;
  line-height: 21px;
  font-weight: 500;
  color: #111827;
`

// Outer strip: full card width, height driven by GaugeArcBox (its only
// in-flow child) — hosts the None/Whole ticks, pinned to its true edges.
export const GaugeWrapper = styled.div`
  position: relative;
  width: 100%;
`

// aspect-ratio (not a fixed height) keeps the arc correctly proportioned at
// any rendered width — recharts clamps radius to min(width, height) / 2, so
// a fixed height was forcing us to separately guess a max-width that
// happened to look right. This is the same technique recharts' own
// "Straight Angle Pie Chart" example uses. Little/Some/Most + the center
// label are positioned as % of *this* box (see GaugeTick below).
export const GaugeArcBox = styled.div`
  position: relative;
  width: 100%;
  max-width: 280px;
  aspect-ratio: 2 / 1;
  margin: 28px auto 0;
`

// Sits on GaugeArcBox's own bottom edge — same baseline the None/Whole
// ticks in the outer GaugeWrapper resolve to (since GaugeArcBox is
// GaugeWrapper's only flow content).
export const GaugeCenterLabel = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  font-size: 24px;
  line-height:36px;
  font-weight: 700;
  pointer-events: none;
  white-space: nowrap;
  z-index: 1;
`

// Positions below are derived from the box's known geometry:
//   outerRadius = 160% of min(width, height)/2
//     → as % of box width:  40%   (160/100 * 25)
//     → as % of box height: 80%   (160/100 * 50)
// A point at angle θ (180° = None/left, 90° = Some/top, 0° = Whole/right)
// sits at: left = 50% + R%w·cos(θ),  top = 100% − R%h·sin(θ)
// Little/Some/Most below use a slightly larger radius (~210–220%) than the
// arc's own 160%, so their labels sit just outside it.
export const GaugeTick = styled.span`
  position: absolute;
  font-size: 12px;
  color: #9ca3af;
  white-space: nowrap;
  pointer-events: none;

  ${({ $tick }) => {
    switch ($tick) {
      // Relative to GaugeWrapper (outer, full card width) — pinned to edges.
      case 'none':
        return `
          left: 0;
          bottom: 4px;
          &::after {
            content: '\\2013';
            margin-left: 4px;
          }
        `
      case 'whole':
        return `
          right: 0;
          bottom: 4px;
          &::before {
            content: '\\2013';
            margin-right: 4px;
          }
        `
      // Relative to GaugeArcBox (inner, aspect-ratio 2:1 box) — trig-derived.
      case 'little':
        return `
          left: 11%;
          top: 22%;
          &::after {
            content: '';
            position: absolute;
            width: 1px;
            height: 9px;
            background: #d1d5db;
            bottom: -11px;
            left: 65%;
            transform: rotate(40deg);
          }
        `
      case 'most':
        return `
          right: 11%;
          top: 22%;
          &::after {
            content: '';
            position: absolute;
            width: 1px;
            height: 9px;
            background: #d1d5db;
            bottom: -11px;
            right: 65%;
            transform: rotate(-40deg);
          }
        `
      case 'some':
        return `
          left: 50%;
          top: -10%;
          transform: translateX(-50%);
          &::after {
            content: '';
            position: absolute;
            width: 1px;
            height: 10px;
            background: #d1d5db;
            bottom: -12px;
            left: 50%;
            transform: translateX(-50%);
          }
        `
      default:
        return ''
    }
  }}
`

// Anchored to a fixed corner — same pattern as Donutchartcard's tooltip,
// never positioned from cursor coordinates.
export const FixedTooltip = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 120px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 10px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  pointer-events: none;
  z-index: 1000;
`

export const FixedTooltipHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 13px;
  color: #111827;
`

export const FixedTooltipDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ color }) => color};
  flex-shrink: 0;
`

export const FixedTooltipValue = styled.div`
  font-size: 13px;
  color: #374151;
`

export const Description = styled.div`
  font-size: 14px;
  color: #444444;
  padding-top: 16px;
  line-height: 18px;
  text-align: center;
`
